import { Link, useLocation } from 'react-router-dom';
import { Sparkles, Calendar, Clock, MapPin, LogOut, Settings, LayoutDashboard, LogIn, HelpCircle, Heart, MessageSquare, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/cn';

const bottomLinkClass =
  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted hover:bg-border hover:text-foreground';

const menuLinks = [
  { href: '/specials', label: 'Specials', icon: Sparkles },
  { href: '/events', label: 'Events', icon: Calendar },
  { href: '/happy-hours', label: 'Happy Hours', icon: Clock },
  { href: '/bars', label: 'Bars', icon: MapPin },
  { href: '/search', label: 'Search', icon: Search },
];

const quickLinks = [
  { href: '/bars?filter=brunch', label: 'Brunch' },
  { href: '/bars?filter=sports', label: 'Sports' },
  { href: '/bars?filter=patio', label: 'Patios' },
  { href: '/bars?filter=dogs', label: 'Dog Friendly' },
];

export function PublicSidebar() {
  const location = useLocation();
  const { isAuthenticated, isAdmin, hasBarAccess, signOut } = useAuth();

  return (
    <aside className="h-screen w-64 shrink-0 border-r border-border bg-background">
      {/* Logo */}
      <div className="h-14 flex items-center justify-center border-b border-border">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo_glass.svg" alt="" className="h-10 w-10" />
          <span style={{ fontFamily: "'Bebas Neue', sans-serif" }} className="text-3xl text-white">
            Bar Guide
          </span>
        </Link>
      </div>

      <nav className="flex h-[calc(100vh-3.5rem)] flex-col p-4">
        {/* Main navigation */}
        <div className="space-y-1">
          {menuLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                location.pathname === link.href
                  ? 'bg-accent-primary/10 text-accent-primary'
                  : 'text-foreground hover:bg-border'
              )}
            >
              <link.icon className="h-5 w-5" />
              {link.label}
            </Link>
          ))}
          <Link
            to="/favorites"
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
              location.pathname === '/favorites'
                ? 'bg-accent-primary/10 text-accent-primary'
                : 'text-foreground hover:bg-border'
            )}
          >
            <Heart className="h-5 w-5" />
            Favorites
          </Link>
        </div>

        {/* Quick links */}
        <div className="mt-8">
          <p className="mb-2 px-3 text-xs font-medium uppercase text-muted">
            Quick Filters
          </p>
          <div className="space-y-1">
            {quickLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="block rounded-lg px-3 py-2 text-sm text-muted hover:bg-border hover:text-foreground"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom section - pushed to bottom */}
        <div className="mt-auto border-t border-border pt-4">
          <div className="space-y-1">
            {isAuthenticated && hasBarAccess && (
              <Link to="/admin" className={bottomLinkClass}>
                <LayoutDashboard className="h-4 w-4" />
                {isAdmin ? 'Admin Dashboard' : 'Manage My Bars'}
              </Link>
            )}
            <Link to="/feedback" className={bottomLinkClass}>
              <MessageSquare className="h-4 w-4" />
              Send Feedback
            </Link>
            <Link to="/settings" className={bottomLinkClass}>
              <Settings className="h-4 w-4" />
              Settings
            </Link>
            <Link to="/help" className={bottomLinkClass}>
              <HelpCircle className="h-4 w-4" />
              Help
            </Link>
            {isAuthenticated ? (
              <button
                onClick={signOut}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-400 hover:bg-border"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-accent-primary hover:bg-border"
              >
                <LogIn className="h-4 w-4" />
                Sign In
              </Link>
            )}
          </div>
          {/* Legal links */}
          <div className="mt-3 flex flex-col items-center gap-1 text-xs text-muted">
            <Link to="/guides" className="hover:text-foreground">
              City Guides
            </Link>
            <div className="flex gap-3">
              <Link to="/privacy" className="hover:text-foreground">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-foreground">
                Terms
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </aside>
  );
}
